// Lazy WebAudio context shared by ding + fanfare. Browsers block context
// creation until a user gesture; we only call this on tap or after a tap-
// induced navigate, so unlocking is reliable. In non-browser environments
// (vitest node env) AudioContext is undefined and the helpers no-op.
//
// When Howler is loaded (samplePlayer), it installs global gesture
// listeners and owns its own AudioContext. Reuse Howler.ctx here so
// synth + samples share one context and one unlock.
import { Howler } from 'howler';

let ctx: AudioContext | null = null;

export function getAudioContext(): AudioContext | null {
  if (Howler.ctx) ctx = Howler.ctx;
  if (!ctx) {
    if (typeof AudioContext === 'undefined') return null;
    ctx = new AudioContext();
  }
  if (ctx.state === 'suspended') {
    // eslint-disable-next-line sonarjs/void-use -- fire-and-forget resume; failure is fine
    void ctx.resume();
  }
  return ctx;
}

// Tone with an exponential decay envelope — short and pleasant.
export function playTone(
  frequency: number,
  durationMs: number,
  startOffsetMs = 0,
  peakGain = 0.18,
  waveform: OscillatorType = 'sine',
): void {
  const c = getAudioContext();
  if (!c) return;
  const start = c.currentTime + startOffsetMs / 1000;
  const end = start + durationMs / 1000;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = waveform;
  osc.frequency.value = frequency;
  gain.gain.setValueAtTime(peakGain, start);
  gain.gain.exponentialRampToValueAtTime(0.0001, end);
  osc.connect(gain).connect(c.destination);
  osc.start(start);
  osc.stop(end);
}
